import type { ReactNode } from 'react';
import './SampleApprovalStatusPill.scss';

export interface SampleStatusPillShellProps {
  /** Leading icon (20px). */
  icon: ReactNode;
  /** The status control rendered after the divider (e.g. a react-select). */
  children: ReactNode;
  /**
   * When set, the icon is a toggle button with this accessible label and fires
   * {@link onToggle}. When omitted, the icon is a static, non-interactive glyph.
   */
  toggleLabel?: string;
  /** Toggle handler; only used when `toggleLabel` is set. */
  onToggle?: () => void;
  /** Expanded shows the divider + children; collapsed shows only the icon. */
  expanded?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * Bordered pill shell for a sample status control (BM-9882) — Figma "Status
 * Selection Dropdown" (node 14575:300684): a 28px unit of `[icon | divider |
 * control]`. The icon is a toggle button when `toggleLabel` is given (licensing /
 * customer columns), or a static glyph otherwise (the existing sample status).
 */
function SampleStatusPillShell({
  icon,
  children,
  toggleLabel,
  onToggle,
  expanded = true,
  className = '',
  'data-testid': dataTestId,
}: SampleStatusPillShellProps) {
  const stateClass = expanded ? 'is-expanded' : 'is-collapsed';
  return (
    <div className={`sample-approval-pill ${stateClass} ${className}`} data-testid={dataTestId}>
      {toggleLabel ? (
        <button
          type="button"
          className="sample-approval-pill__toggle"
          aria-label={toggleLabel}
          aria-pressed={expanded}
          onClick={onToggle}
        >
          {icon}
        </button>
      ) : (
        <span className="sample-approval-pill__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      {expanded && (
        <>
          <span className="sample-approval-pill__divider" aria-hidden="true" />
          {children}
        </>
      )}
    </div>
  );
}

export default SampleStatusPillShell;
